import { createFlags, type FeatureFlags, type FlagKey, type FlagValues } from '@flobi/artwork-core';
import { useEffect, useState } from 'react';
import { artworkCmd } from '../services/apiClient';

/** How many more times the flags are asked for after a refusal: at 1 s, 2 s and 4 s. */
const FLAG_RETRIES = 3;

/**
 * What this workspace's plan allows.
 *
 * Every capability has a flag from the first milestone, so a plan can switch one off
 * without touching feature code. The backend decides the values — its answer comes from
 * the plan in billing — and the engine enforces the ones that change a document, so an
 * agent calling commands directly cannot get around a capability that is switched off.
 *
 * Until the answer arrives the flags are the engine's defaults rather than everything
 * off, so the editor does not flash a stripped-down toolbar on every load.
 */
export function useFlags(): FeatureFlags & { readonly values: Readonly<Record<FlagKey, boolean>>; readonly loaded: boolean } {
  const [values, setValues] = useState<FlagValues>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    /**
     * Asked again before giving up. The gateway's workspace lookup sometimes fails on a
     * page load and the answer is "Choose a workspace", and the editor then ran the whole
     * session on the defaults without a sign — which, once flags come from a paid plan,
     * is a paid workspace quietly losing features (QA 2026-09-24, files-doc-F14).
     */
    const ask = (attempt: number) => {
      void artworkCmd<Record<string, boolean>>('artwork.flags.get', {})
        .then((answer) => {
          if (stopped) return;
          setValues(answer as FlagValues);
          setLoaded(true);
        })
        .catch(() => {
          if (stopped) return;
          if (attempt < FLAG_RETRIES) {
            timer = setTimeout(() => ask(attempt + 1), 1000 * 2 ** attempt);
            return;
          }
          // A workspace whose flags cannot be read gets the defaults, not a dead editor.
          setLoaded(true);
        });
    };
    ask(0);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, []);

  const flags = createFlags(values);
  return { ...flags, loaded };
}
